// Fleet page logic (fleet-1): readiness per vessel, worst first, with its top reason; what is due in the next 30 / 60 / 90 days;
// map label placement; the vessel a fleet chat line is about; the manager's name inside a vessel. Plain JS: tests/fleet-1.test.mjs.
// The score is the Dashboard's (src/lib/readiness.js, readinessInputs) - same rows, same rules, so the fleet ring and the ring
// on the vessel's own Dashboard show the same number.
import { readiness, readinessInputs, ringTone } from './readiness.js'
import { expiryStatus } from './expiry.js'

const val = v => {
  if (v == null) return ''
  if (typeof v === 'object' && !Array.isArray(v)) return String(v.value || v.name || v.label || '')
  if (Array.isArray(v)) return v.length ? val(v[0]) : ''
  return String(v)
}
const isoOf = v => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(val(v)); return m ? m[0] : null }
// days from today to the date: 0 = today, 5 = in 5 days, -3 = 3 days ago (null = no date)
export const daysTo = (v, today) => { const d = isoOf(v); return d && today ? Math.round((Date.parse(d) - Date.parse(today)) / 864e5) : null }
export const windowOf = days => (days == null || days < 0 ? null : days <= 30 ? 30 : days <= 60 ? 60 : days <= 90 ? 90 : null)
const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many)
const st = r => val(r && r['Status'])
const titleOf = r => val(r && (r['Title'] || r['Document Name'] || r['Name'])) || 'Document'

// "Safety Equipment Certificate" -> "SEC": the names a manager reads at a glance; anything else, shortened
const SHORT = [[/safety equipment/i, 'SEC'], [/safety management/i, 'SMC'], [/safety radio/i, 'SRC'], [/safety construction/i, 'SCC'], [/load ?line/i, 'Load Line'],
  [/document of compliance|\bdoc\b/i, 'DOC'], [/minimum safe manning|safe manning/i, 'Safe Manning'], [/\bmlc\b|maritime labour/i, 'MLC'], [/\biopp\b|oil pollution/i, 'IOPP'],
  [/\bissc\b|ship security/i, 'ISSC'], [/registry|registration/i, 'Registry'], [/radio licen/i, 'Radio licence'], [/tonnage/i, 'Tonnage cert']]
export function shortDoc(name) {
  const s = String(name || '').trim()
  for (const [re, short] of SHORT) if (re.test(s)) return short
  const t = s.replace(/\s+certificate$/i, '')
  return t.length > 26 ? t.slice(0, 25) + '…' : t || 'Document'
}

// one vessel. d = rows as fetchRows gives them (display names) + drills (drillsNeedingAttention) + ras (risk_assessments rows) + ismMajor
//   see(module) -> the module is in this vessel's plan (a management login has Full on every module of its vessels)
export function vesselHealth(d, { today, see = () => true } = {}) {
  const docs = d.docs || [], crewDocs = d.crewDocs || [], eq = d.eq || [], drills = d.drills || [], ras = d.ras || []
  const inp = readinessInputs(d, see, today)
  const rd = readiness(inp)
  const sv = m => !!see(m)
  // the reason line: the biggest penalties first, said the way a manager says them
  const expDocs = sv('documents') ? docs.filter(x => expiryStatus(x['Expiry Date'], today).expired) : []   // the shared rule (src/lib/expiry.js)
  const oos = sv('equipment') ? eq.filter(x => st(x) === 'Out of Service') : []
  const drillsLate = sv('checklists') ? drills.filter(x => x.status === 'overdue') : []
  const SAY = {
    maintenance: () => plural(inp.tasksOverdue, 'overdue task', 'overdue tasks'),
    docs: () => (expDocs.length === 1 ? shortDoc(titleOf(expDocs[0])) + ' expired' : expDocs.length === 2 ? shortDoc(titleOf(expDocs[0])) + ' + ' + shortDoc(titleOf(expDocs[1])) + ' expired' : plural(expDocs.length, 'ship cert expired', 'ship certs expired')),
    crewExpired: () => plural(inp.crewExpired, 'crew cert expired', 'crew certs expired'),
    crewSoon: () => plural(inp.crewSoon, 'crew cert', 'crew certs') + ' < 30 d',
    drillsOverdue: () => (drillsLate.length === 1 ? drillsLate[0].label + ' drill overdue' : plural(drillsLate.length, 'drill overdue', 'drills overdue')),
    drillsDue: () => plural(inp.drillsDue, 'drill due', 'drills due'),
    equipment: () => (oos.length === 1 ? (val(oos[0]['Equipment Name'] || oos[0]['Name']) || 'Equipment') + ' out of service' : plural(oos.length, 'item out of service', 'items out of service')),
    stock: () => plural(inp.outOfStock, 'item out of stock', 'items out of stock'),
    ismMajor: () => plural(inp.ismMajor, 'major NC open', 'major NCs open'),
  }
  const reasons = rd.parts.map(p => (SAY[p.key] ? SAY[p.key]() : p.label))
  // an RA review overdue does not lower the score (readiness.js) but a manager wants to see it
  const raLate = sv('risk') ? ras.filter(r => { const n = daysTo(r.next_review_due, today); return n != null && n < 0 && !/archiv|superseded|withdrawn/i.test(String(r.status || '')) }) : []
  if (raLate.length) reasons.push(plural(raLate.length, 'RA review overdue', 'RA reviews overdue'))
  const due = dueSoon(d, { today, see })
  const next = due[0]
  const reason = reasons.length ? reasons.slice(0, 2).join(' · ') : 'All clear' + (next && next.days <= 30 ? ' · ' + next.short + ' ' + inDays(next.days) : '')
  return { score: rd.score, tone: ringTone(rd.score), rd, reason, reasons, due, raLate }
}
export const inDays = n => (n === 0 ? 'today' : n === 1 ? 'tomorrow' : 'in ' + n + ' days')

// what falls due in the next 90 days (today included), soonest first: ship certificates, surveys (documents.survey_due), crew
// certificates, drills due this month, risk assessment reviews. Already expired / overdue items are not here (the red lists).
export function dueSoon(d, { today, see = () => true } = {}) {
  const out = []
  const add = (kind, name, date, verb, extra = {}) => { const days = daysTo(date, today); const w = windowOf(days); if (w) out.push({ kind, name, short: kind === 'doc' || kind === 'survey' ? shortDoc(name) : name, date: isoOf(date), days, window: w, label: name + ' · ' + verb + ' ' + inDays(days), ...extra }) }
  if (see('documents')) for (const r of d.docs || []) {
    if (!expiryStatus(r['Expiry Date'], today).expired) add('doc', titleOf(r), r['Expiry Date'], 'expires')
    add('survey', titleOf(r), r['Survey Due'], 'survey due')
  }
  if (see('crew')) for (const r of d.crewDocs || []) if (!expiryStatus(r['Expiry Date'], today).expired) add('crew', titleOf(r), r['Expiry Date'], 'expires')
  if (see('checklists')) for (const x of d.drills || []) if (x.status === 'due') out.push({ kind: 'drill', name: x.label + ' drill', short: x.label + ' drill', date: null, days: 0, window: 30, label: x.label + ' drill · due this month' })
  if (see('risk')) for (const r of d.ras || []) {
    if (/archiv|superseded|withdrawn/i.test(String(r.status || ''))) continue
    add('ra', [r.ra_number, r.activity].filter(Boolean).join(' ') || 'Risk assessment', r.next_review_due, 'review due')
  }
  return out.sort((a, b) => a.days - b.days || a.name.localeCompare(b.name))
}

// the Overview order: worst readiness first; a vessel still loading goes last, in the fleet's own order
export function worstFirst(fleet, health) {
  const idx = new Map(fleet.map((v, i) => [String(v.id), i]))
  const sc = v => (health[v.id] ? health[v.id].score : Infinity)
  return [...fleet].sort((a, b) => sc(a) - sc(b) || idx.get(String(a.id)) - idx.get(String(b.id)))
}
export const TONE_COLOR = { good: '#35c98b', mid: '#e0a23a', low: '#e5484d' }

// map labels: for each marker pick the side (right, left, above, below) whose label box hits no other label and no other marker.
// pts = [{ id, x, y, w, h }] in screen pixels (x, y = marker centre; w, h = the label's size); r = marker radius, gap = marker to label;
// width / height = the map's size. No free side: the side covering the least (overlap area + area outside the map)
export function placeLabels(pts, { r = 17, gap = 8, width = Infinity, height = Infinity } = {}) {
  const boxOf = (p, side) => {
    const d = r + gap
    if (side === 'right') return { x1: p.x + d, y1: p.y - p.h / 2, x2: p.x + d + p.w, y2: p.y + p.h / 2 }
    if (side === 'left') return { x1: p.x - d - p.w, y1: p.y - p.h / 2, x2: p.x - d, y2: p.y + p.h / 2 }
    if (side === 'up') return { x1: p.x - p.w / 2, y1: p.y - d - p.h, x2: p.x + p.w / 2, y2: p.y - d }
    return { x1: p.x - p.w / 2, y1: p.y + d, x2: p.x + p.w / 2, y2: p.y + d + p.h }
  }
  // how much a label box covers other labels / markers, and how much of it sticks out of the map
  // (6 px of slack: the labels have padding and a shadow, so boxes that only touch at their edges read fine)
  const area = (a, b) => Math.max(0, Math.min(a.x2, b.x2) - Math.max(a.x1, b.x1) - 6) * Math.max(0, Math.min(a.y2, b.y2) - Math.max(a.y1, b.y1) - 6)
  const outside = b => (b.x2 - b.x1) * (b.y2 - b.y1) - area(b, { x1: 0, y1: 0, x2: width, y2: height })
  const markers = pts.map(p => ({ id: p.id, x1: p.x - r, y1: p.y - r, x2: p.x + r, y2: p.y + r }))
  const placed = [], out = {}
  // top-most first (then left-most): stable, and the northern boat keeps the usual right-hand label
  for (const p of [...pts].sort((a, b) => a.y - b.y || a.x - b.x)) {
    let best = null, bestCost = Infinity
    for (const side of ['right', 'left', 'up', 'down']) {
      const b = boxOf(p, side)
      const cost = placed.reduce((n, q) => n + area(b, q), 0) + markers.reduce((n, m) => n + (m.id !== p.id ? area(b, m) : 0), 0) + outside(b)
      if (cost < bestCost) { best = side; bestCost = cost }
      if (cost === 0) break
    }
    out[p.id] = best
    placed.push(boxOf(p, best))
  }
  return out
}

// a line of a fleet answer -> the fleet vessel it is about (the first vessel named in it; the longest name wins a tie such as
// "M/Y Ball" inside "M/Y Ballerina"), or null. "Corvina" alone matches "M/Y Corvina".
export function fleetLineVessel(line, fleet) {
  const s = String(line || '').toLowerCase()
  if (!s.trim() || !(fleet || []).length) return null
  let best = null
  for (const v of fleet) {
    const full = String(v.vessel_name || '').toLowerCase().trim()
    if (!full) continue
    const bare = full.replace(/^(m\/y|s\/y|m\/v|my|sy|mv)\s+/, '')
    for (const n of [full, bare]) {
      if (n.length < 3) continue
      const re = new RegExp('(^|[^a-z0-9])' + n.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&') + '(?![a-z0-9])')
      const m = re.exec(s)
      if (m && (!best || m.index < best.at || (m.index === best.at && n.length > best.len))) best = { v, at: m.index, len: n.length }
    }
  }
  return best ? best.v : null
}

// the manager inside a vessel (useAuth: _companyView) or at fleet level (accountType 'company')
export const isManager = config => !!(config && (config._companyView || config.accountType === 'company'))
// "Blue Meridian Yacht Management" -> "Blue Meridian" (the greeting); the full name stays on records
export function shortCompany(name) {
  const s = String(name || '').trim()
  const t = s.replace(/[,.]?\s+(yacht|yachts|yachting|marine|superyacht)?\s*(management|managers|services|group)?\s*(ltd|limited|llc|inc|sarl|s\.?a\.?r?\.?l?\.?|gmbh|bv|plc)?\.?$/i, '').trim()
  return t || s
}
// who the login is, as records write it (approved by, reported by, posted by): crew name, the company for a manager, else the fallback
export const actorName = (config, fallback = 'Captain') => (config && config.crewName) || (isManager(config) ? (config.memberLabel || config.companyName || 'Management') : fallback)   // a company member by name (fleet-4)
// the Dashboard / chat greeting name: "Blue Meridian" for a manager, else the given name (Captain / the crew role)
export const greetName = (config, fallback = 'Captain') => (isManager(config) ? shortCompany(config.companyName) || 'Fleet Manager' : fallback)
